import { useMemo } from 'react';
import { ModalSize, ModalTemplate } from '@ardoq/modal';
import { ViewpointBuilderNavigation } from './viewpointBuilderNavigation/ViewpointBuilderNavigation';
import { SelectContextTabContent } from './selectContextTab/SelectContextTabContent';
import { MetamodelViewContent } from './metaModel/MetamodelViewContent';
import {
  ViewpointBuilderViewState,
  ViewpointBuilderCommands,
  ViewpointBuilderContext,
} from './types';
import {
  getViewpointBuilderEventListeners,
  ViewpointBuilderEventListeners,
} from './viewpointBuilderEventListeners';
import { AddFiltersTabContent } from './addFiltersTab/AddFiltersTabContent';
import { BuildSubgraphContent } from './traversals/BuildSubgraphContent';
import { MetaInfoContent } from './viewpointMetaInfo/MetaInfoContent';
import { GroupingRulesContent } from './addGroupingRulesTab/GroupingRulesContent';
import { ConditionalFormattingContent } from './formattingTabs/ConditionalFormattingContent';
import { LabelFormattingContent } from './formattingTabs/LabelFormattingContent';
import {
  ContextConfig,
  getContextConfig,
} from 'viewpointBuilder/openViewpointBuilder/getContextConfig';
import { SelectContextComponentTypeTabContent } from './selectContextTab/SelectContextComponentTypeTabContent';
import { ResizablePageBody } from '@ardoq/page-layout';
import { CollapsePathsTabContent } from './collapsePathsTab/CollapsePathsTabContent';
import { RequiredComponentsTabContent } from './requiredComponentsTab/RequiredComponentsTabContent';
import { viewpointApi } from '@ardoq/api';
import { t } from '@ardoq/i18n';
import { viewpointBuilderTracking } from './viewpointBuilderTracking';

type ViewpointBuilderProps = {
  state: ViewpointBuilderViewState;
  commands: ViewpointBuilderCommands;
  closeViewpointBuilder: () => void;
  context: ViewpointBuilderContext;
};

type ViewpointBuilderContentProps = Omit<ViewpointBuilderProps, 'context'> & {
  contextConfig: ContextConfig;
  viewpointBuilderEventListeners: ViewpointBuilderEventListeners;
};

const ViewpointBuilderContent = ({
  commands,
  state,
  closeViewpointBuilder,
  contextConfig,
  viewpointBuilderEventListeners,
}: ViewpointBuilderContentProps) => {
  const { i18nLocale, locale } = state;
  switch (state.viewpointBuilderNavigationState.selectedTab) {
    case 'SELECT_CONTEXT_COMPONENT_INSTANCES_TAB':
      return (
        <SelectContextTabContent
          closeViewpointBuilder={closeViewpointBuilder}
          state={state.searchComponentsState}
          commands={commands.componentsSearchCommands}
          primaryButtonConfig={state.primaryButtonConfig}
          contextConfig={contextConfig}
          viewpointName={state.metaInfoState.name}
          i18nLocale={i18nLocale}
          namespace={state.namespace}
        />
      );

    case 'SELECT_CONTEXT_COMPONENT_TYPE_TAB':
      return (
        <SelectContextComponentTypeTabContent
          closeViewpointBuilder={closeViewpointBuilder}
          state={state.searchComponentsState}
          commands={commands.componentsSearchCommands}
          primaryButtonConfig={state.primaryButtonConfig}
          i18nLocale={i18nLocale}
        />
      );

    case 'SUBGRAPH_TAB':
      return (
        <BuildSubgraphContent
          state={state.editTraversalsState}
          commands={commands.editTraversalCommands}
          onCancel={closeViewpointBuilder}
          primaryButtonConfig={state.primaryButtonConfig}
          label={contextConfig.graphViewTitle}
          i18nLocale={i18nLocale}
        />
      );
    case 'COLLAPSE_PATHS_TAB':
      return (
        <CollapsePathsTabContent
          state={state.editTraversalsState}
          commands={commands.collapsePathsCommands}
          closeViewpointBuilder={closeViewpointBuilder}
          primaryButtonConfig={state.primaryButtonConfig}
          label={contextConfig.graphViewTitle}
          toggleGraphNodeSelection={
            viewpointBuilderEventListeners.collapsePathsNodeSelection
          }
          graphNodeOnHover={
            viewpointBuilderEventListeners.collapsedPathsGraphNodeOnHover
          }
          i18nLocale={i18nLocale}
        />
      );
    case 'REQUIRED_COMPONENTS_TAB':
      return (
        <RequiredComponentsTabContent
          state={state.editTraversalsState}
          commands={commands.requiredComponentsCommands}
          closeViewpointBuilder={closeViewpointBuilder}
          primaryButtonConfig={state.primaryButtonConfig}
          label={contextConfig.graphViewTitle}
          toggleGraphNodeSelection={
            viewpointBuilderEventListeners.requiredComponentsNodeSelection
          }
          graphNodeOnHover={
            viewpointBuilderEventListeners.requiredComponentsGraphNodeOnHover
          }
          i18nLocale={i18nLocale}
        />
      );
    case 'FILTERS_TAB':
      return (
        <AddFiltersTabContent
          closeViewpointBuilder={closeViewpointBuilder}
          state={state.editTraversalsState}
          commands={commands.filterCommands}
          label={contextConfig.graphViewTitle}
          toggleGraphNodeSelection={
            viewpointBuilderEventListeners.toggleGraphNodeSelectionInFilters
          }
          primaryButtonConfig={state.primaryButtonConfig}
          locale={locale}
          i18nLocale={i18nLocale}
        />
      );

    case 'META_INFO_TAB': {
      return (
        <MetaInfoContent
          closeViewpointBuilder={closeViewpointBuilder}
          state={state.metaInfoState}
          /* paths is a temporarily added prop needed in the AI generate description-function.
           * This function will be moved to a routine instead of the React component, and the
           * paths-prop will be removed at that time */
          paths={state.editTraversalsState.stateAsSavableAttributes.paths}
          graphData={{
            graphNodes: state.editTraversalsState.graphNodes,
            graphEdges: state.editTraversalsState.graphEdges,
            graphGroups: state.editTraversalsState.graphGroups,
          }}
          commands={{
            ...commands.metaInfoCommands,
            ...commands.selectedViewCommands,
          }}
          generateViewpointDescription={viewpointApi.generateDescription}
          primaryButtonConfig={state.primaryButtonConfig}
          label={contextConfig.graphViewTitle}
          i18nLocale={i18nLocale}
        />
      );
    }

    case 'GROUPS_TAB':
      return (
        <GroupingRulesContent
          closeViewpointBuilder={closeViewpointBuilder}
          state={state.groupingsState}
          commands={commands.groupingsCommands}
          primaryButtonConfig={state.primaryButtonConfig}
          i18nLocale={i18nLocale}
        />
      );

    case 'FORMATTING_TAB':
      return (
        <ConditionalFormattingContent
          closeViewpointBuilder={closeViewpointBuilder}
          state={state.formattingState.conditionalFormatting}
          primaryButtonConfig={state.primaryButtonConfig}
          i18nLocale={i18nLocale}
        />
      );

    case 'LABELS_TAB':
      return (
        <LabelFormattingContent
          closeViewpointBuilder={closeViewpointBuilder}
          primaryButtonConfig={state.primaryButtonConfig}
          multiLabelFormattingProps={state.formattingState.multiLabelFormatting}
          i18nLocale={i18nLocale}
        />
      );

    case 'METAMODEL_TAB':
      return (
        <MetamodelViewContent
          closeViewpointBuilder={closeViewpointBuilder}
          state={state.metamodelViewState}
          toggleGraphNodeSelection={
            viewpointBuilderEventListeners.toggleGraphNodeSelectionInMetamodelView
          }
          commands={commands.metamodelViewCommands}
          i18nLocale={i18nLocale}
        />
      );

    default:
      state.viewpointBuilderNavigationState.selectedTab satisfies never;
      return null;
  }
};

const ViewpointBuilder = ({
  state,
  commands,
  closeViewpointBuilder,
  context,
}: ViewpointBuilderProps) => {
  const contextConfig = getContextConfig(context, state.i18nLocale);
  const viewpointBuilderEventListeners = useMemo(
    () => getViewpointBuilderEventListeners(commands),
    [commands]
  );
  const onCloseButtonClick = () => {
    viewpointBuilderTracking.trackCloseViewpointBuilder();
    closeViewpointBuilder();
  };
  return (
    <ModalTemplate
      modalSize={ModalSize.COVER}
      headerText={
        contextConfig.isNavigationPanelHidden && state.metaInfoState.name
          ? t(
              state.i18nLocale,
              'viewpointBuilder:header.openViewpointTitle',
              { viewpointName: state.metaInfoState.name },
              'Viewpoint: {{viewpointName}}'
            )
          : contextConfig.modalTitle
      }
      onCloseButtonClick={onCloseButtonClick}
      closeButtonLabel={t(
        state.i18nLocale,
        'viewpointBuilder:header.closeWithEsc',
        'Close (Esc)'
      )}
    >
      <ResizablePageBody
        skipPadding
        leftContent={
          !contextConfig.isNavigationPanelHidden && (
            <ViewpointBuilderNavigation
              state={state.viewpointBuilderNavigationState}
              commands={commands.navigationCommands}
            />
          )
        }
        leftContentInitialWidth={450}
      >
        <ViewpointBuilderContent
          state={state}
          commands={commands}
          closeViewpointBuilder={closeViewpointBuilder}
          contextConfig={contextConfig}
          viewpointBuilderEventListeners={viewpointBuilderEventListeners}
        />
      </ResizablePageBody>
    </ModalTemplate>
  );
};

export { ViewpointBuilder };
